import { toast } from '@avila-roffe/components-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useChatUser } from '../ChatUserContext';
import {
  type CreateSessionPayload,
  closeSession,
  createSession,
  deleteSession,
  fetchSession,
  fetchSessions,
  updateSession,
} from '../chatApi';
import { chatKeys } from '../chatKeys';

// ─── useSessions ──────────────────────────────────────────────────────────────

export function useSessions() {
  const { tenantSlug } = useChatUser();

  return useQuery({
    queryKey: chatKeys.sessionsList(tenantSlug),
    queryFn: () => fetchSessions(),
    staleTime: 30_000,
  });
}

// ─── useSession ───────────────────────────────────────────────────────────────

export function useSession(id: string | null) {
  return useQuery({
    queryKey: chatKeys.session(id ?? ''),
    queryFn: () => fetchSession(id as string),
    enabled: !!id,
    staleTime: 60_000,
  });
}

// ─── useCreateSession ─────────────────────────────────────────────────────────

export function useCreateSession() {
  const qc = useQueryClient();
  const { tenantSlug } = useChatUser();

  return useMutation({
    mutationFn: (payload: CreateSessionPayload) => createSession(payload),
    onSuccess: (newSession) => {
      const key = chatKeys.sessionsList(tenantSlug);
      qc.setQueryData<import('../chatApi').ChatSession[]>(key, (old = []) => [newSession, ...old]);
      void qc.invalidateQueries({ queryKey: key });
      toast.success('Session created');
    },
  });
}

// ─── useCloseSession ──────────────────────────────────────────────────────────

export function useCloseSession() {
  const qc = useQueryClient();
  const { tenantSlug } = useChatUser();

  return useMutation({
    mutationFn: (id: string) => closeSession(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: chatKeys.sessionsList(tenantSlug) });
      void qc.invalidateQueries({ queryKey: chatKeys.session(id) });
      toast.success('Session closed');
    },
  });
}

// ─── useDeleteSession ─────────────────────────────────────────────────────────

export function useDeleteSession() {
  const qc = useQueryClient();
  const { tenantSlug } = useChatUser();

  return useMutation({
    mutationFn: (id: string) => deleteSession(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: chatKeys.sessionsList(tenantSlug) });
      qc.removeQueries({ queryKey: chatKeys.session(id) });
      toast.success('Session deleted');
    },
  });
}

// ─── useUpdateSession ─────────────────────────────────────────────────────────

export function useUpdateSession() {
  const qc = useQueryClient();
  const { tenantSlug } = useChatUser();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { title?: string } }) =>
      updateSession(id, patch),
    onSuccess: (updated, { id }) => {
      qc.setQueryData(chatKeys.session(id), updated);
      void qc.invalidateQueries({ queryKey: chatKeys.sessionsList(tenantSlug) });
    },
  });
}
